const existingWishlist = window.PTTEKWishlist;

if (existingWishlist) {
  existingWishlist.refresh(document);
} else {
  // Shopify App Proxy base path: storefront calls stay same-origin under /apps/pttek.
  const APP_PROXY_BASE = '/apps/pttek';
  const STORAGE_KEY = 'pttek.wishlist.product_ids';
  const TOGGLE_SELECTOR = '[data-wishlist-toggle]';
  const PRODUCT_ID_PATTERN = /^\d+$/;
  const MAX_WISHLIST_ITEMS = 200;
  const MAX_PRODUCT_ID_LENGTH = 32;
  const REQUEST_TIMEOUT_MS = 10000;
  const state = {
    productIds: new Set(),
    pendingProductIds: new Set(),
    mutatedProductIds: new Set(),
    controllers: new Set(),
    initialized: false,
    hydrated: false,
    mutationVersion: 0
  };

  const ready = (callback) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
    else callback();
  };
  const normalizeProductId = (value) => String(value || '').trim();
  const isValidProductId = (value) => {
    const productId = normalizeProductId(value);
    return productId.length > 0 && productId.length <= MAX_PRODUCT_ID_LENGTH && PRODUCT_ID_PATTERN.test(productId);
  };
  const uniqueProductIds = (productIds) => {
    const seen = new Set();
    const ids = [];
    productIds.forEach((value) => {
      const productId = normalizeProductId(value);
      if (ids.length < MAX_WISHLIST_ITEMS && isValidProductId(productId) && !seen.has(productId)) {
        seen.add(productId);
        ids.push(productId);
      }
    });
    return ids;
  };
  const readLocalWishlist = () => {
    try {
      const storedWishlist = localStorage.getItem(STORAGE_KEY) || '[]';
      const parsed = JSON.parse(storedWishlist);
      const productIds = Array.isArray(parsed) ? uniqueProductIds(parsed) : [];
      if (storedWishlist !== JSON.stringify(productIds)) writeLocalWishlist(productIds);
      return productIds;
    } catch {
      return [];
    }
  };
  const writeLocalWishlist = (productIds) => {
    const boundedProductIds = uniqueProductIds(productIds);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(boundedProductIds));
    } catch {
      return null;
    }
    return boundedProductIds;
  };
  const updateLocalWishlistProduct = (productId, selected) => {
    const productIds = new Set(readLocalWishlist());
    if (selected) {
      if (!productIds.has(productId) && productIds.size >= MAX_WISHLIST_ITEMS) return false;
      productIds.add(productId);
    } else {
      productIds.delete(productId);
    }
    writeLocalWishlist([...productIds]);
    return true;
  };
  const removeLocalWishlistProducts = (productIds) => {
    const productIdsSet = new Set(productIds);
    writeLocalWishlist(readLocalWishlist().filter((productId) => !productIdsSet.has(productId)));
  };
  const buttonsFrom = (root) => {
    const buttons = [];
    if (root instanceof Element && root.matches(TOGGLE_SELECTOR)) buttons.push(root);
    root.querySelectorAll?.(TOGGLE_SELECTOR).forEach((button) => buttons.push(button));
    return buttons;
  };
  const isLoggedIn = () => (
    document.body?.dataset.wishlistLoggedIn === 'true' ||
    document.querySelector(`${TOGGLE_SELECTOR}[data-wishlist-logged-in="true"]`) !== null
  );
  const responseProductIds = (data) => (Array.isArray(data?.product_ids) ? uniqueProductIds(data.product_ids) : []);
  const fetchJson = async (path, options = {}) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    state.controllers.add(controller);
    try {
      const response = await fetch(`${APP_PROXY_BASE}${path}`, {
        ...options,
        credentials: 'same-origin',
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          ...(options.body ? { 'Content-Type': 'application/json' } : {}),
          ...(options.headers || {})
        }
      });
      if (!response.ok) {
        const error = new Error(`Wishlist request failed: ${response.status}`);
        error.status = response.status;
        throw error;
      }
      return response.json();
    } finally {
      window.clearTimeout(timeoutId);
      state.controllers.delete(controller);
    }
  };
  const setProductIds = (productIds) => {
    state.productIds = new Set(uniqueProductIds(productIds));
    refresh(document);
  };
  const setProductSelected = (productId, selected) => {
    const nextProductIds = new Set(state.productIds);
    if (selected) {
      if (!nextProductIds.has(productId) && nextProductIds.size >= MAX_WISHLIST_ITEMS) return false;
      nextProductIds.add(productId);
    } else {
      nextProductIds.delete(productId);
    }
    state.productIds = nextProductIds;
    refresh(document);
    return true;
  };
  const applyHydratedProductIds = (productIds, hydrationVersion) => {
    const hydratedProductIds = new Set(uniqueProductIds(productIds));
    if (state.mutationVersion === hydrationVersion && state.pendingProductIds.size === 0) {
      setProductIds([...hydratedProductIds]);
      state.mutatedProductIds.clear();
      return;
    }

    const nextProductIds = new Set(state.productIds);
    buttonsFrom(document).forEach((button) => {
      const productId = normalizeProductId(button.dataset.wishlistProductId);
      if (
        !isValidProductId(productId) ||
        state.pendingProductIds.has(productId) ||
        state.mutatedProductIds.has(productId)
      ) return;
      if (hydratedProductIds.has(productId)) nextProductIds.add(productId);
      else nextProductIds.delete(productId);
    });
    hydratedProductIds.forEach((productId) => {
      if (!state.pendingProductIds.has(productId) && !state.mutatedProductIds.has(productId)) {
        nextProductIds.add(productId);
      }
    });
    setProductIds([...nextProductIds]);
  };
  const updateButton = (button) => {
    const productId = normalizeProductId(button.dataset.wishlistProductId);
    if (!isValidProductId(productId)) return;
    const selected = state.productIds.has(productId);
    const busy = state.pendingProductIds.has(productId);
    const label = selected ? button.dataset.wishlistRemoveLabel : button.dataset.wishlistAddLabel;
    button.setAttribute('aria-pressed', String(selected));
    button.toggleAttribute('disabled', busy);
    button.setAttribute('aria-busy', String(busy));
    button.querySelectorAll('[data-wishlist-icon]').forEach((node) => {
      node.setAttribute('fill', selected ? 'currentColor' : 'none');
    });
    if (label) {
      button.setAttribute('aria-label', label);
      button.querySelectorAll('[data-wishlist-label]').forEach((node) => { node.textContent = label; });
    }
    button.dataset.wishlistState = selected ? 'selected' : 'idle';
  };
  function refresh(root = document) {
    buttonsFrom(root).forEach(updateButton);
  }
  const hydrateFromServer = async () => {
    if (!isLoggedIn() || state.hydrated || !document.querySelector(TOGGLE_SELECTOR)) return;
    state.hydrated = true;
    const hydrationVersion = state.mutationVersion;
    const guestProductIds = readLocalWishlist();
    setProductIds(guestProductIds);

    try {
      if (guestProductIds.length > 0) {
        const merged = await fetchJson('/wishlist/merge', {
          method: 'POST',
          body: JSON.stringify({ product_ids: guestProductIds })
        });
        removeLocalWishlistProducts(guestProductIds);
        applyHydratedProductIds(responseProductIds(merged), hydrationVersion);
        return;
      }

      const data = await fetchJson('/wishlist');
      applyHydratedProductIds(responseProductIds(data), hydrationVersion);
    } catch (error) {
      console.error('Wishlist could not be loaded', error);
      applyHydratedProductIds(guestProductIds, hydrationVersion);
    }
  };
  const setProductBusy = (productId, busy) => {
    if (busy) state.pendingProductIds.add(productId);
    else state.pendingProductIds.delete(productId);
    refresh(document);
  };
  const toggleLocal = (productId) => {
    const selected = state.productIds.has(productId);
    if (!setProductSelected(productId, !selected)) return;
    state.mutationVersion += 1;
    state.mutatedProductIds.add(productId);
    updateLocalWishlistProduct(productId, !selected);
  };
  const toggleRemote = async (productId) => {
    if (state.pendingProductIds.has(productId)) return;
    const selected = state.productIds.has(productId);
    const nextSelected = !selected;
    if (!nextSelected && !state.productIds.has(productId)) return;
    if (nextSelected && state.productIds.size >= MAX_WISHLIST_ITEMS) return;

    state.mutationVersion += 1;
    state.mutatedProductIds.add(productId);
    setProductBusy(productId, true);
    setProductSelected(productId, nextSelected);

    try {
      await fetchJson('/wishlist/items', {
        method: selected ? 'DELETE' : 'POST',
        body: JSON.stringify({ product_id: productId })
      });
      updateLocalWishlistProduct(productId, false);
    } catch (error) {
      if (error.status === 409 || error.status === 422) {
        setProductSelected(productId, selected);
      } else {
        console.error('Wishlist could not be updated', error);
        updateLocalWishlistProduct(productId, nextSelected);
      }
    } finally {
      setProductBusy(productId, false);
      refresh(document);
    }
  };
  const handleClick = (event) => {
    const button = event.target.closest(TOGGLE_SELECTOR);
    if (!button) return;
    const productId = normalizeProductId(button.dataset.wishlistProductId);
    if (!isValidProductId(productId)) return;
    event.preventDefault();

    if (isLoggedIn()) toggleRemote(productId);
    else toggleLocal(productId);
  };
  const handleSectionLoad = (event) => {
    refresh(event.target);
    hydrateFromServer();
  };
  const handleSectionUnload = (event) => {
    buttonsFrom(event.target).forEach((button) => {
      button.toggleAttribute('disabled', false);
      button.setAttribute('aria-busy', 'false');
    });
  };
  const init = () => {
    if (state.initialized) return;
    state.initialized = true;
    state.productIds = new Set(readLocalWishlist());
    refresh(document);
    hydrateFromServer();
    document.addEventListener('click', handleClick);
    document.addEventListener('shopify:section:load', handleSectionLoad);
    document.addEventListener('shopify:section:unload', handleSectionUnload);
  };
  function destroy() {
    document.removeEventListener('click', handleClick);
    document.removeEventListener('shopify:section:load', handleSectionLoad);
    document.removeEventListener('shopify:section:unload', handleSectionUnload);
    state.controllers.forEach((controller) => controller.abort());
    state.controllers.clear();
    state.pendingProductIds.clear();
    state.initialized = false;
  }
  const handlePageShow = (event) => {
    if (!event.persisted) return;
    state.hydrated = false;
    state.mutatedProductIds.clear();
    init();
  };

  window.PTTEKWishlist = { refresh, destroy };
  window.addEventListener('pageshow', handlePageShow);
  window.addEventListener('pagehide', destroy);
  window.addEventListener('unload', destroy);
  ready(init);
}
